import {useEffect, useRef, useState} from "react";
import {Button, Input, Modal, QRCode} from "antd";
import {useSetRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import {browserSupportsWebAuthn, startAuthentication} from "@simplewebauthn/browser";
import {createLog} from "../api/restful/logs/logs";
import {globalNotificationState} from "./global_state";
import METHOD_ICONS from "./twoFactorMethodIcons";
import {backendMessage} from "./backendMessage";
import useTwoFactorGuard, {useTwoFactorAction} from "./useTwoFactorGuard";
import {forgetPasskeyWorkedHere, markPasskeyWorkedHere} from "./passkeyPromptState";
import usePasskeyEnrolment from "./usePasskeyEnrolment";
import {
    buildTelegramLinkUrl,
    buildWhatsappLinkUrl,
    waitForTelegramLink,
    waitForWhatsappLink,
} from "./telegramLink";
import ProviderLinkModal from "./providerLinkModal";
import {startLivenessEnrolmentMutation} from "../api/graphql/userAuthProvider";
import {openCaptureWindow, pollLiveness} from "./livenessCeremony";
import {
    restartLivenessChallengeMutation,
    resendTwoFactorChallengeMutation,
    switchTwoFactorChallengeMethodMutation,
    verifyTwoFactorChallengeMutation,
} from "../api/graphql/twoFactorChallenge";

const RESEND_COOLDOWN_SECONDS = 60;

// the server decides when another code may be asked for, and it shortens the wait for the
// first one; mirroring that rule here would only drift from it
const secondsUntil = (value) =>
    value
        ? Math.max(Math.ceil((new Date(value).getTime() - Date.now()) / 1000), 0)
        : RESEND_COOLDOWN_SECONDS;

// a challenge in one of these states can never succeed again, so the dialog closes
// rather than sitting there offering a verify and a resend that both fail
const TERMINAL_KEYS = [
    "admin.err.two_factor_attempts_exhausted",
    "admin.err.two_factor_expired",
    "admin.err.two_factor_invalid_state",
];

// each switch raises a fresh challenge, and the server allows ten in ten minutes. a few
// tries is a client who cannot receive one; more is someone burning the whole allowance
const MAX_METHOD_SWITCHES = 3;

// face id never carries the vendor anywhere a person is being verified; the vendor name
// lives only on the crm two factor settings pages, under twoFactor.methodLivenessVendor
const methodLabel = (t, method) =>
    method === "liveness"
        ? t("twoFactor.faceId", {defaultValue: "Face ID"})
        : t(`twoFactor.method${method.charAt(0).toUpperCase()}${method.slice(1)}`);

// one dialog for every guarded action, mounted once and driven by the guard
const TwoFactorChallengeModal = () => {
    const {challenge, onVerified, onCancel} = useTwoFactorAction();
    const {t} = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    // switching replaces the challenge, so the modal holds its own
    const [active, setActive] = useState(challenge);
    const [switching, setSwitching] = useState(false);
    const [switches, setSwitches] = useState(0);
    // the app sets no antd colorPrimary, so the focus ring would come out default blue
    const [focused, setFocused] = useState(false);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [livenessBusy, setLivenessBusy] = useState(false);
    // bumped to abandon a poll whose challenge was cancelled, switched or superseded
    const pollRun = useRef(0);
    // a capture url survives one open, so the next attempt must mint its own
    const spent = useRef(false);
    // set while the client finishes on their phone: there is no tab of ours to watch, so
    // this is the only sign the wait is still wanted
    const [phoneUrl, setPhoneUrl] = useState(null);
    // a link the popup blocker refused, kept so the next click opens it inside the gesture
    const blockedEnrolment = useRef(null);
    const inFlight = useRef(false);
    // timing guards can still let a fast second click through, so each attempt goes once
    const submitted = useRef(null);
    const resending = useRef(false);
    const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
    const inputRef = useRef(null);

    // a rule may name a method this client has not set up
    const setupMethod = active?.setupRequired;
    // a device that proves unable to finish flips the dialog to the another-device path
    const {enrol, busy: enroling} = usePasskeyEnrolment({
        onBlocked: () => setPlatformAuthenticator(false),
    });
    const guard = useTwoFactorGuard();
    const [linking, setLinking] = useState(false);
    const [linkUrl, setLinkUrl] = useState(null);
    const [linkProviderName, setLinkProviderName] = useState(null);
    const [platformAuthenticator, setPlatformAuthenticator] = useState(null);

    const PROVIDER_LINK = {
        telegram: {build: buildTelegramLinkUrl, watch: waitForTelegramLink},
        whatsapp: {build: buildWhatsappLinkUrl, watch: waitForWhatsappLink},
    };

    const linkProvider = async (provider) => {
        const {build, watch} = PROVIDER_LINK[provider] || {};

        if (!build) return;

        setLinking(true);

        try {
            const started = await guard.run({
                action: "providerLink",
                subAction: provider,
                binding: {destination: provider},
                attempt: (challengeId) => build(challengeId),
            });

            if (!started) return;

            const {url, errKey} = started;

            if (!url) {
                notify("error", t(errKey), "twoFactorProviderLinkError");
                return;
            }

            setLinkUrl(url);
            setLinkProviderName(provider);

            watch(() => {
                setLinkUrl(null);
                notify("success", t("linkedAccounts.linkSuccess"), "twoFactorProviderLinked");
                onCancel?.();
            });
        } catch (error) {
            notify("error", t("admin.err.system_error"), "twoFactorProviderLinkException");
        } finally {
            setLinking(false);
        }
    };

    // the url the challenge came with is spent the moment it is opened, so every attempt
    // after the first asks the server for a new capture session
    // the url is the same whichever device finishes the check, so getting one is separate
    // from deciding where to open it. null means it has already been reported to the client
    const livenessUrl = async () => {
        if (!spent.current) {
            try {
                return JSON.parse(active?.options || "{}")?.verificationUrl || null;
            } catch (error) {
                return null;
            }
        }

        const response = await restartLivenessChallengeMutation({
            challengeId: active.challengeId,
        });
        const payload = response?.restartLivenessChallenge;

        if (payload?.__typename !== "LivenessRestartPayload") {
            notify(
                "error",
                backendMessage(t, payload?.errKey, "admin.err.two_factor_liveness_failed"),
                "twoFactorLivenessRestartFailed",
            );

            if (TERMINAL_KEYS.includes(payload?.errKey)) onCancel?.();
            return null;
        }

        return JSON.parse(payload.options || "{}")?.verificationUrl || null;
    };

    // the capture page is served to whoever holds the link, so a client whose device has no
    // camera can finish on their phone. the verdict is read by our server either way
    const verifyOnPhone = async () => {
        if (inFlight.current) return;

        inFlight.current = true;
        setBusy(true);

        try {
            const url = await livenessUrl();

            if (!url) {
                notify("error", t("admin.err.two_factor_liveness_failed"), "twoFactorLivenessNoLink");
                return;
            }

            spent.current = true;
            setPhoneUrl(url);

            // no window of ours to watch, so this waits for a verdict or the challenge expiry
            const settled = await pollLiveness(active.challengeId, active.expiresAt, pollRun, null);

            if (!settled) return;

            if (settled.__typename === "BaseError") {
                notify("error", backendMessage(t, settled.errKey), "twoFactorLivenessFailed");

                if (TERMINAL_KEYS.includes(settled.errKey)) onCancel?.();
                return;
            }

            onVerified?.(active.challengeId);
        } catch (error) {
            // swallowing this into a generic toast hides the only clue there is
            console.log(">>> Face ID phone handoff failed:", error?.message, error);
            notify("error", t("admin.err.system_error"), "twoFactorLivenessException");
        } finally {
            setPhoneUrl(null);
            inFlight.current = false;
            setBusy(false);
        }
    };

    const verifyLiveness = async () => {
        if (inFlight.current) return;

        const captureWindow = openCaptureWindow(t);

        inFlight.current = true;
        setBusy(true);

        try {
            const url = await livenessUrl();

            if (!url) {
                captureWindow?.close();
                return;
            }

            if (!captureWindow || captureWindow.closed) {
                notify(
                    "error",
                    t("twoFactor.livenessPopupBlocked", {
                        defaultValue: "Allow pop-ups for this site, then try again.",
                    }),
                    "twoFactorLivenessPopupBlocked",
                );
                return;
            }

            // replace, so back does not land on the placeholder
            captureWindow.location.replace(url);
            spent.current = true;

            const settled = await pollLiveness(
                active.challengeId,
                active.expiresAt,
                pollRun,
                captureWindow,
            );

            if (!settled) return;

            if (settled.__typename === "LivenessAbandoned") {
                notify(
                    "error",
                    t("twoFactor.livenessAbandoned", {
                        defaultValue: "The camera check was closed before it finished. Try again.",
                    }),
                    "twoFactorLivenessAbandoned",
                );
                return;
            }

            if (settled.__typename === "BaseError") {
                notify("error", backendMessage(t, settled.errKey), "twoFactorLivenessFailed");

                if (TERMINAL_KEYS.includes(settled.errKey)) onCancel?.();
                return;
            }

            onVerified?.(active.challengeId);
        } catch (error) {
            captureWindow?.close();
            notify("error", t("admin.err.system_error"), "twoFactorLivenessException");
        } finally {
            // always released: a stuck spinner is the one state with no way out of the dialog
            inFlight.current = false;
            setBusy(false);
        }
    };

    // enrolment is guarded in its own right, so it runs through the guard like a link does.
    // the capture tab is opened only once there is a url for it, so the email code is asked
    // for first rather than behind a blank tab the client did not ask for yet
    const enrolLiveness = async () => {
        if (livenessBusy) return;

        // a link the blocker refused opens straight from this click, with no await in front
        // of it, so the gesture still counts
        const held = blockedEnrolment.current;
        let win = held ? window.open(held.url, "_blank") : null;

        setLivenessBusy(true);

        try {
            let started = held;

            if (!started) {
                started = await guard.run({
                    action: "livenessEnrolment",
                    attempt: async (challengeId) => {
                        const response = await startLivenessEnrolmentMutation(challengeId);
                        const payload = response?.startLivenessEnrolment;

                        // errObj carries the terms the resolver will re-check, so the guard
                        // asks for the challenge on those rather than on ours
                        return payload?.__typename === "BaseError" || !payload?.url
                            ? {
                                  url: null,
                                  errKey: payload?.errKey || "admin.err.system_error",
                                  errObj: payload?.errObj,
                              }
                            : {...payload, errKey: null};
                    },
                });

                if (!started) return;

                if (!started.url) {
                    notify("error", backendMessage(t, started.errKey), "twoFactorLivenessEnrolError");
                    return;
                }

                win = window.open(started.url, "_blank");
            }

            if (!win) {
                // the ceremony is already minted, so the retry reopens it rather than paying
                // for another one
                blockedEnrolment.current = started;
                notify(
                    "error",
                    t("twoFactor.livenessPopupBlocked", {
                        defaultValue: "Allow pop-ups for this site, then try again.",
                    }),
                    "twoFactorLivenessPopupBlocked",
                );
                return;
            }

            blockedEnrolment.current = null;

            const settled = await pollLiveness(
                started.challengeId,
                started.expiresAt,
                pollRun,
                win,
            );

            if (!settled) return;

            if (settled.__typename === "LivenessAbandoned") {
                notify(
                    "error",
                    t("twoFactor.livenessAbandoned", {
                        defaultValue: "The camera check was closed before it finished. Try again.",
                    }),
                    "twoFactorLivenessEnrolAbandoned",
                );
                return;
            }

            if (settled.__typename === "BaseError") {
                notify("error", backendMessage(t, settled.errKey), "twoFactorLivenessEnrolFailed");
                return;
            }

            // the provider row is written by the verification service and can lag the
            // verdict, so this says it is on its way rather than claiming it is live
            notify(
                "success",
                t("twoFactor.livenessEnrolled", {
                    defaultValue: "Face ID is being activated. Please try again in a moment.",
                }),
                "twoFactorLivenessEnrolled",
            );
            onCancel?.();
        } catch (error) {
            win?.close();
            notify("error", t("admin.err.system_error"), "twoFactorLivenessEnrolException");
        } finally {
            setLivenessBusy(false);
        }
    };

    const copyLivenessLink = async () => {
        if (!phoneUrl) return;

        try {
            await navigator.clipboard.writeText(phoneUrl);
            notify("success", t("twoFactor.linkCopied", {defaultValue: "Link copied"}), "twoFactorLivenessLinkCopied");
        } catch (error) {
            notify("error", t("admin.err.system_error"), "twoFactorLivenessCopyFailed");
        }
    };

    // an in-app browser can never enrol, so the way forward is to reopen the page elsewhere
    const copyLink = async () => {
        const url = window.location.href;

        try {
            await navigator.clipboard.writeText(url);
        } catch (error) {
            const field = document.createElement("textarea");

            field.value = url;
            document.body.appendChild(field);
            field.select();
            document.execCommand("copy");
            field.remove();
        }

        notify("success", t("twoFactor.linkCopied", {defaultValue: "Link copied"}), "twoFactorLinkCopied");
    };

    // signs on the device rather than receiving anything, so there is no code and no resend
    const signs = active?.requiresDelivery === false;
    // signs too, but through a camera in another tab rather than on this device
    const isLiveness = active?.method === "liveness";

    const notify = (status, msg, key) => {
        // wrapped because logging must never break the flow it is observing
        if (status === "error") {
            try {
                createLog({
                    status: "warning",
                    module: "TwoFactor",
                    submodule: "ChallengeModal",
                    function: key || "notify",
                    description: typeof msg === "string" ? msg : "",
                    logData: {action: active?.action, method: active?.method, challengeId: active?.challengeId},
                    triggerBy: "user",
                });
            } catch (ignore) {}
        }
        setNotificationMessage({status, title: "", msg, key});
    };

    useEffect(() => {
        // a poll outliving its challenge would resolve an action the client walked away from
        pollRun.current += 1;
        spent.current = false;
        setPhoneUrl(null);
        setActive(challenge);
        // the modal mounts once globally, so the budget must not outlive the challenge
        setSwitches(0);
    }, [challenge]);

    useEffect(() => {
        if (setupMethod !== "passkey") return;

        // an in-app browser has no webauthn at all, which is not the same as a device
        // with no screen lock: that one can still enrol by phone, this one cannot
        if (!window.PublicKeyCredential) {
            setPlatformAuthenticator("unsupported");
            return;
        }

        window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
            .then(setPlatformAuthenticator)
            .catch(() => setPlatformAuthenticator(false));
    }, [setupMethod]);

    useEffect(() => {
        if (!active) return;

        setCode("");
        setBusy(false);
        setCooldown(secondsUntil(active.resendAvailableAt));

        // the dialog outlives every challenge it shows, so its guards belong to this one
        inFlight.current = false;
        submitted.current = null;

        if (!signs) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [active, signs]);

    useEffect(() => {
        if (!active || signs || cooldown <= 0) return;

        const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
        return () => clearTimeout(timer);
    }, [active, signs, cooldown]);

    const submit = async (value) => {
        const response = await verifyTwoFactorChallengeMutation({
            challengeId: active?.challengeId,
            code: value,
        });

        const failure = response?.verifyTwoFactorChallenge;

        if (failure?.__typename === "BaseError") {
            notify("error", t(failure.errKey), "twoFactorVerifyError");

            if (TERMINAL_KEYS.includes(failure.errKey)) {
                onCancel?.();
            }

            return false;
        }

        return true;
    };

    const verifyCode = async () => {
        const attempt = active?.challengeId + ":" + code;

        if (inFlight.current || submitted.current === attempt || code.length < 6) return;

        submitted.current = attempt;
        inFlight.current = true;
        setBusy(true);

        try {
            // the guard has still to retry the action, so the button stays busy until the modal goes
            if (await submit(code)) {
                onVerified?.(active?.challengeId);
                return;
            }

            // cleared so it can be retyped, and focus waits out the disabled render
            submitted.current = null;
            setCode("");
            setTimeout(() => inputRef.current?.focus(), 100);
        } catch (error) {
            // nothing came back, so the same code has to stay usable
            submitted.current = null;
            notify("error", t("admin.err.system_error"), "twoFactorVerifyException");
        }

        inFlight.current = false;
        setBusy(false);
    };

    const verifySigned = async () => {
        if (inFlight.current || !active?.options) return;

        if (!browserSupportsWebAuthn()) {
            notify(
                "error",
                t("twoFactor.passkeyBrowserUnsupported", {
                    defaultValue: "This browser doesn't support passkeys. Verify another way below.",
                }),
                "passkeyBrowserUnsupported",
            );

            return;
        }

        inFlight.current = true;
        setBusy(true);

        let assertion;

        try {
            assertion = await startAuthentication({optionsJSON: JSON.parse(active.options)});
        } catch (error) {
            console.log("Passkey verification failed:", error?.name, error?.message);
            forgetPasskeyWorkedHere();

            if (error?.name === "SecurityError") {
                // an origin or rp mismatch is ours, and retrying cannot help
                notify(
                    "error",
                    t("twoFactor.passkeyConfigError", {
                        defaultValue:
                            "Passkey sign-in is not set up correctly for this site. Please verify another way and contact support.",
                    }),
                    "twoFactorPasskeyFailed",
                );
            } else {
                // nothing tells a missing passkey from a declined one, and cancelling is a
                // choice, so the wording blames neither and the tiles below stay the way out
                notify(
                    "error",
                    t("twoFactor.passkeyFailed", {
                        defaultValue:
                            "Passkey verification was not completed. Try again, or verify another way.",
                    }),
                    "twoFactorPasskeyFailed",
                );
            }

            inFlight.current = false;
            setBusy(false);
            return;
        }

        try {
            if (await submit(JSON.stringify(assertion))) {
                markPasskeyWorkedHere();
                onVerified?.(active?.challengeId);
                return;
            }
        } catch (error) {
            notify("error", t("admin.err.system_error"), "twoFactorVerifyException");
        }

        inFlight.current = false;
        setBusy(false);
    };

    useEffect(() => {
        if (code.length === 6) {
            verifyCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const resend = async () => {
        if (cooldown > 0 || resending.current) return;

        resending.current = true;

        try {
            const response = await resendTwoFactorChallengeMutation({
                challengeId: active?.challengeId,
            });

            const failure = response?.resendTwoFactorChallenge;

            if (failure?.__typename === "BaseError") {
                notify("error", t(failure.errKey), "twoFactorResendError");

                if (TERMINAL_KEYS.includes(failure.errKey)) {
                    onCancel?.();
                }

                return;
            }

            setCooldown(secondsUntil(response.resendTwoFactorChallenge?.resendAvailableAt));
        } catch (error) {
            notify("error", t("admin.err.system_error"), "twoFactorResendException");
        } finally {
            resending.current = false;
        }
    };

    // the brand's order picks the first method, not the only one
    // offering a tile that leads straight to an error is worse than not offering it
    const otherMethods = (active?.availableMethods || []).filter(
        (method) =>
            method !== active?.method &&
            (method !== "passkey" || browserSupportsWebAuthn()),
    );

    const switchesLeft = MAX_METHOD_SWITCHES - switches;

    const switchMethod = async (method) => {
        if (switching || switchesLeft <= 0) return;

        setSwitching(true);

        try {
            const response = await switchTwoFactorChallengeMethodMutation({
                challengeId: active?.challengeId,
                method,
            });
            const payload = response?.switchTwoFactorChallengeMethod;

            if (!payload || payload?.__typename === "BaseError") {
                notify(
                    "error",
                    backendMessage(t, payload?.errKey),
                    "twoFactorSwitchError",
                );
                return;
            }

            setActive(payload);
            setSwitches((count) => count + 1);
        } catch (error) {
            notify("error", t("admin.err.system_error"), "twoFactorSwitchException");
        } finally {
            setSwitching(false);
        }
    };

    const passkeyUnsupported = setupMethod === "passkey" && platformAuthenticator === "unsupported";

    if (setupMethod) {
        return (
            <>
            <ProviderLinkModal url={linkUrl} provider={linkProviderName} onClose={() => setLinkUrl(null)} />

            <Modal
                open={!linkUrl}
                zIndex={1100}
                title={t("twoFactor.setupRequiredTitle", {defaultValue: "One more step"})}
                onCancel={onCancel}
                footer={null}
                destroyOnClose
                maskClosable={false}
                width={400}
            >
                <div style={{display: "flex", flexDirection: "column", gap: 18, alignItems: "center"}}>
                    <div
                        style={{
                            width: 96,
                            padding: "16px 8px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 8,
                            border: "1px solid var(--divider-color)",
                            borderRadius: 10,
                            background: "var(--primary-card-bg-color)",
                            color: "var(--primary-text-color)",
                        }}
                    >
                        <span style={{height: 24, display: "flex", alignItems: "center"}}>
                            {METHOD_ICONS[setupMethod]}
                        </span>
                        <span style={{fontSize: 12}}>{methodLabel(t, setupMethod)}</span>
                    </div>

                    <div
                        style={{
                            fontSize: 13,
                            color: "var(--note-text-color)",
                            lineHeight: 1.5,
                            textAlign: "center",
                        }}
                    >
                        {passkeyUnsupported
                            ? t("twoFactor.passkeyUnsupportedBrowser", {
                                  defaultValue:
                                      "Passkeys aren't supported in this browser. Open the site in Chrome or Safari to continue.",
                              })
                            : t("twoFactor.setupRequired_" + setupMethod, {
                                  defaultValue:
                                      setupMethod === "passkey"
                                          ? "This action can only be confirmed with a passkey. Set one up to continue."
                                          : setupMethod === "liveness"
                                            ? "This action needs a camera check of your face. Set up Face ID to continue."
                                            : "This action can only be confirmed on Telegram. Link it to continue.",
                              })}
                    </div>

                    <Button
                        type={"primary"}
                        block
                        loading={
                            setupMethod === "passkey"
                                ? enroling
                                : setupMethod === "liveness"
                                    ? livenessBusy
                                    : linking
                        }
                        onClick={
                            passkeyUnsupported
                                ? copyLink
                                : setupMethod === "passkey"
                                    ? enrol
                                    : setupMethod === "liveness"
                                        ? enrolLiveness
                                        : () => linkProvider(setupMethod)
                        }
                        style={{
                            // the dialog is a portal, out of reach of the brand stylesheet
                            backgroundColor: "var(--primary-btn-bg-color)",
                            color: "var(--primary-btn-text-color)",
                            borderColor: "var(--primary-btn-border-color)",
                        }}
                    >
                        {passkeyUnsupported
                            ? t("twoFactor.copyLink", {defaultValue: "Copy link"})
                            : setupMethod === "passkey"
                                ? t("twoFactor.setUpPasskey", {defaultValue: "Set up passkey"})
                                : setupMethod === "liveness"
                                    ? t("twoFactor.setUpLiveness", {defaultValue: "Set up Face ID"})
                                    : setupMethod === "whatsapp"
                                        ? t("twoFactor.linkWhatsapp", {defaultValue: "Link WhatsApp"})
                                        : t("twoFactor.linkTelegram", {defaultValue: "Link Telegram"})}
                    </Button>

                    {/* shown only where the device gives us reason to doubt, so the common
                        case stays a single instruction and one button */}
                    {setupMethod === "passkey" && platformAuthenticator === false && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={copyLink}
                            onKeyDown={(event) =>
                                (event.key === "Enter" || event.key === " ") && copyLink()
                            }
                            style={{
                                textAlign: "center",
                                fontSize: 13,
                                cursor: "pointer",
                                color: "var(--link-color, var(--primary-btn-bg-color))",
                            }}
                        >
                            {t("twoFactor.openOnAnotherDevice", {
                                defaultValue: "Open on another device",
                            })}
                        </div>
                    )}
                </div>
            </Modal>
            </>
        );
    }

    return (
        <>
        <ProviderLinkModal url={linkUrl} provider={linkProviderName} onClose={() => setLinkUrl(null)} />

        <Modal
            open={!!active}
            zIndex={1100}
            title={t("twoFactor.verifyTitle", {defaultValue: "Confirm it's you"})}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            maskClosable={false}
            width={400}
        >
            <div style={{display: "flex", flexDirection: "column", gap: 18}}>
                {/* --note-text-color is near invisible on the darker brands */}
                <div style={{fontSize: 13, color: "var(--primary-text-color)", opacity: 0.85, lineHeight: 1.5}}>
                    {signs ? (
                        isLiveness
                            ? t("twoFactor.livenessPrompt", {
                                  defaultValue:
                                      "We will open your camera in a new tab to check it is you. Keep this page open.",
                              })
                            : t("twoFactor.passkeyPrompt", {
                                  defaultValue: "Use your passkey to confirm it's you",
                              })
                    ) : (
                        <>
                            {t("twoFactor.codeSentToPrefix", {defaultValue: "We sent a code to"})}{" "}
                            <span style={{color: "var(--primary-text-color)", fontWeight: 500}}>
                                {active?.destinationMasked || ""}
                            </span>
                        </>
                    )}
                </div>

                {!signs && (
                    <Input
                        ref={inputRef}
                        maxLength={6}
                        inputMode={"numeric"}
                        autoComplete={"one-time-code"}
                        value={code}
                        disabled={busy}
                        // the tracking pushes the run right, so the same amount pads it back
                        style={{
                            letterSpacing: 8,
                            paddingLeft: 8,
                            textAlign: "center",
                            fontSize: 16,
                            background: "var(--form-input-bg-color)",
                            borderColor: focused
                                ? "var(--primary-btn-bg-color)"
                                : "var(--form-input-border-color)",
                            boxShadow: "none",
                        }}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                        onPressEnter={verifyCode}
                    />
                )}

                {phoneUrl ? (
                    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 12}}>
                        {/* white plate: a qr has to stay light to scan on the dark brands */}
                        <div style={{background: "#fff", padding: 12, borderRadius: 8}}>
                            {/* antd defaults the code to token.colorText, which is near
                                white on the dark brands and vanishes on this plate */}
                            <QRCode
                                value={phoneUrl}
                                size={168}
                                bordered={false}
                                color={"#000000"}
                                bgColor={"#ffffff"}
                            />
                        </div>

                        <div style={{fontSize: 12, textAlign: "center", opacity: 0.85, lineHeight: 1.5}}>
                            {t("twoFactor.livenessScanHint", {
                                defaultValue:
                                    "Scan this with your phone to run the camera check. Keep this page open.",
                            })}
                        </div>

                        <div
                            role={"button"}
                            tabIndex={0}
                            onClick={copyLivenessLink}
                            onKeyDown={(event) =>
                                (event.key === "Enter" || event.key === " ") && copyLivenessLink()
                            }
                            style={{fontSize: 12, cursor: "pointer", color: "var(--primary-btn-bg-color)"}}
                        >
                            {t("twoFactor.copyLink", {defaultValue: "Copy link"})}
                        </div>
                    </div>
                ) : null}

                <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                    <Button
                        type={"primary"}
                        block
                        loading={busy}
                        disabled={busy || (signs ? !active?.options : code.length < 6)}
                        onClick={signs ? (isLiveness ? verifyLiveness : verifySigned) : verifyCode}
                        style={{
                            backgroundColor: "var(--primary-btn-bg-color)",
                            color: "var(--primary-btn-text-color)",
                            borderColor: "var(--primary-btn-border-color)",
                            // the brand colour would otherwise paint over antd's disabled
                            // state and leave it looking pressable
                            opacity: (signs ? !active?.options : code.length < 6) ? 0.5 : 1,
                        }}
                    >
                        {signs
                            ? isLiveness
                                ? t("twoFactor.verifyWithLiveness", {defaultValue: "Verify with Face ID"})
                                : t("twoFactor.verifyWithPasskey", {defaultValue: "Verify with passkey"})
                            : t("twoFactor.verify", {defaultValue: "Verify"})}
                    </Button>

                    {/* a desktop with no camera cannot finish here, and the capture page is
                        served to whoever holds the link */}
                    {isLiveness && !phoneUrl ? (
                        <div
                            role={"button"}
                            tabIndex={0}
                            onClick={verifyOnPhone}
                            onKeyDown={(event) =>
                                (event.key === "Enter" || event.key === " ") && verifyOnPhone()
                            }
                            style={{
                                textAlign: "center",
                                fontSize: 13,
                                padding: "4px 0",
                                cursor: busy ? "not-allowed" : "pointer",
                                opacity: busy ? 0.5 : 1,
                                color: "var(--primary-btn-bg-color)",
                            }}
                        >
                            {t("twoFactor.livenessUsePhone", {defaultValue: "Scan with phone"})}
                        </div>
                    ) : null}

                    {!signs && (
                        <div
                            role={"button"}
                            tabIndex={0}
                            onClick={resend}
                            onKeyDown={(event) =>
                                (event.key === "Enter" || event.key === " ") && resend()
                            }
                            style={{
                                textAlign: "center",
                                fontSize: 13,
                                cursor: cooldown > 0 ? "default" : "pointer",
                                opacity: cooldown > 0 ? 0.65 : 1,
                                color:
                                    cooldown > 0
                                        ? "var(--primary-text-color)"
                                        : "var(--link-color, var(--primary-btn-bg-color))",
                            }}
                        >
                            {cooldown > 0
                                ? t("twoFactor.resendIn", {seconds: cooldown, defaultValue: "Send a new code in {{seconds}}s"})
                                : t("twoFactor.resend", {defaultValue: "Send a new code"})}
                        </div>
                    )}
                </div>

                {otherMethods.length > 0 && (
                    <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                        <div style={{display: "flex", alignItems: "center", gap: 12}}>
                            <span style={{flexGrow: 1, height: 1, background: "var(--divider-color)"}} />
                            <span
                                style={{
                                    fontSize: 11,
                                    letterSpacing: "0.04em",
                                    color: "var(--note-text-color)",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {t("twoFactor.tryAnotherWay", {defaultValue: "Or verify another way"})}
                            </span>
                            <span style={{flexGrow: 1, height: 1, background: "var(--divider-color)"}} />
                        </div>

                        <div style={{display: "flex", gap: 10}}>
                            {otherMethods.map((method) => (
                                <div
                                    key={method}
                                    role={"button"}
                                    tabIndex={switchesLeft > 0 ? 0 : -1}
                                    onClick={() => switchMethod(method)}
                                    onKeyDown={(event) =>
                                        (event.key === "Enter" || event.key === " ") &&
                                        switchMethod(method)
                                    }
                                    style={{
                                        flexGrow: 1,
                                        flexBasis: 0,
                                        minWidth: 0,
                                        padding: "14px 8px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 8,
                                        border: "1px solid var(--divider-color)",
                                        borderRadius: 10,
                                        background: "var(--primary-card-bg-color)",
                                        cursor: switching || switchesLeft <= 0 ? "not-allowed" : "pointer",
                                        opacity: switching || switchesLeft <= 0 ? 0.45 : 1,
                                        color: "var(--primary-text-color)",
                                    }}
                                >
                                    <span style={{height: 24, display: "flex", alignItems: "center"}}>
                                        {METHOD_ICONS[method]}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%",
                                        }}
                                    >
                                        {methodLabel(t, method)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {switchesLeft <= 0 && (
                            <div style={{textAlign: "center", fontSize: 12, color: "var(--note-text-color)"}}>
                                {t("twoFactor.noSwitchesLeft", {
                                    defaultValue: "Send a new code, or close and start again",
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
        </>
    );
};

export default TwoFactorChallengeModal;
